import { RSAA } from 'redux-api-middleware'
import { getApiPath, makeQueryParams } from 'sidecar-utility-library'
import { getAuthedUser } from 'sidecar-utilities'
import { types } from './index'
import { getNextRequestId } from './utils/getNextRequestId'
import { isStaleResponse } from './utils/isStaleResponse'

// Default pharmacy provider for NCPDP API calls (per LIGHTS-4207)
const DEFAULT_PHARMACY_PROVIDER = 'costPlus'

/**
 * Fetch individual pharmacy details by NCPDP
 *
 * @param {Object} params
 * @param {string} params.ncpdp - National Council for Prescription Drug
 *   Programs ID
 * @param {string} params.ndc - National Drug Code
 * @param {number} params.quantity - Prescription quantity
 * @param {boolean} params.mailOrder - Whether pharmacy is mail order
 * @param {string} [params.memberUuid] - Member UUID (optional, will be derived
 *   from state)
 * @returns {Object} RSAA action
 */
export const fetchPharmacyByNcpdp = ({
  ncpdp,
  ndc,
  quantity,
  mailOrder,
  memberUuid: explicitMemberUuid,
}) => {
  // Tag every phase of this request with the same id so the reducer can tell
  // whether an arriving response is still the one the page is waiting for.
  const meta = { requestId: getNextRequestId('pharmacy') }

  return {
    [RSAA]: {
      endpoint: state => {
        // Derive memberUuid from state if not provided
        const memberUuid =
          explicitMemberUuid ||
          state.Auth?.authedUser?.uuid ||
          getAuthedUser()?.uuid

        const queryParams = makeQueryParams({
          ncpdp,
          ndc,
          quantity,
          mailOrder: mailOrder ? 'true' : 'false',
          memberUuid,
          pharmacy: DEFAULT_PHARMACY_PROVIDER,
        })

        return getApiPath('care', `cares/price-by-ncpdp${queryParams}`)
      },
      method: 'GET',
      types: [
        { type: types.FETCH_PHARMACY_BY_NCPDP_REQUEST, meta },
        { type: types.FETCH_PHARMACY_BY_NCPDP_SUCCESS, meta },
        { type: types.FETCH_PHARMACY_BY_NCPDP_ERROR, meta },
      ],
    },
  }
}

/** Clear pharmacy detail state */
export const clearPharmacyDetail = () => ({
  type: types.CLEAR_PHARMACY_DETAIL,
})

/** Reducer for pharmacy detail state */
const pharmacyReducer = (state, action) => {
  const { type, payload, meta = {} } = action

  switch (type) {
    case types.FETCH_PHARMACY_BY_NCPDP_REQUEST:
      return {
        ...state,
        pharmacy: {
          ...state.pharmacy,
          busy: true,
          error: null,
          requestId: meta.requestId ?? null,
        },
      }

    case types.FETCH_PHARMACY_BY_NCPDP_SUCCESS:
      // A price lookup for a pharmacy the user has already navigated away from
      // must not overwrite the one they're looking at now (BASH-219).
      if (isStaleResponse(meta.requestId, state.pharmacy.requestId))
        return state

      return {
        ...state,
        pharmacy: {
          busy: false,
          error: null,
          entity: payload,
          requestId: state.pharmacy.requestId,
        },
      }

    case types.FETCH_PHARMACY_BY_NCPDP_ERROR: {
      if (isStaleResponse(meta.requestId, state.pharmacy.requestId))
        return state

      // Extract error message from various possible payload structures
      let errorMessage = 'Failed to fetch pharmacy details. Please try again.'

      if (payload) {
        if (payload.response?.message) {
          errorMessage = payload.response.message
        } else if (payload.message) {
          errorMessage = payload.message
        } else if (payload.response?.data?.message) {
          errorMessage = payload.response.data.message
        } else if (payload.response?.status) {
          errorMessage = `Error ${payload.response.status}: Failed to fetch pharmacy details`
        } else if (typeof payload === 'string') {
          errorMessage = payload
        }
      }

      return {
        ...state,
        pharmacy: {
          ...state.pharmacy,
          busy: false,
          error: errorMessage,
        },
      }
    }

    case types.CLEAR_PHARMACY_DETAIL:
      return {
        ...state,
        pharmacy: {
          busy: false,
          error: null,
          entity: null,
          // Clearing also abandons anything in flight: a response stamped with
          // the old id no longer matches, so it will be discarded on arrival.
          requestId: null,
        },
      }

    default:
      return state
  }
}

/** Selectors */
export const selectPharmacyDetail = state => state.Care?.pharmacy?.entity
export const selectPharmacyBusy = state => state.Care?.pharmacy?.busy || false
export const selectPharmacyError = state => state.Care?.pharmacy?.error

export default pharmacyReducer
